import React, { useState, useEffect } from "react";
import { useNotifications } from "./NotificationsContext";

import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import {
  getFonctions,
  getGares,
  getDistricts,
  checkMatricule,
  checkTelephone,
  checkCin,
} from "../api/api";

export default function AdherentModal({ show, onHide, onSave }) {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fonctions, setFonctions] = useState([]);
  const [gares, setGares] = useState([]);
  const [districts, setDistricts] = useState([]);
  const { addNotification } = useNotifications();

  const [validationStatus, setValidationStatus] = useState({
    matricule: { checking: false, available: null, message: "" },
    cin: { checking: false, available: null, message: "" },
    telephone: { checking: false, available: null, message: "" },
  });

  const [data, setData] = useState({
    first_name: "",
    last_name: "",
    matricule: "",
    cin: "",
    telephone: "",
    situation_familiale: "celibataire",
    fonction: "",
    gare: "",
    district: "",
    password: "",
    parents: [],
    enfants: [],
  });

  const [passwordDisplay, setPasswordDisplay] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  // =========================
  //   Génération mot de passe
  // =========================
  const generatePassword = () => {
    const newPass = Math.random().toString(36).slice(-10);
    setPasswordDisplay(newPass);
    setData((d) => ({ ...d, password: newPass }));
    setPasswordCopied(false);  };

  // =========================
  //   Initialiser PARENTS (toujours)
  // =========================
  const initParentsIfNeeded = () => {
    setData((prev) => {
      if (prev.parents && prev.parents.length > 0) return prev;
      const nomFamille = prev.last_name || "";      return {
        ...prev,
        parents: [
          {
            type_parent: "pere",
            nom: nomFamille,
            prenom: "",
            date_naissance: "",
            situation: "vivant",
          },
          {
            type_parent: "mere",
            nom: nomFamille,
            prenom: "",
            date_naissance: "",
            situation: "vivant",
          },
        ],
      };
    });
  };

  // =========================
  //   Chargement des listes
  // =========================
  useEffect(() => {
    if (!show) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [foncRes, gareRes, distRes] = await Promise.all([
          getFonctions(),
          getGares(),
          getDistricts(),
        ]);
        setFonctions(foncRes.data);
        setGares(gareRes.data);
        setDistricts(distRes.data);      } catch (err) {
        console.error("❌ Erreur chargement listes:", err);
      } finally {
        setLoading(false);
      }
    };

    setValidationStatus({
      matricule: { checking: false, available: null, message: "" },
      cin: { checking: false, available: null, message: "" },
      telephone: { checking: false, available: null, message: "" },
    });
    setErrors({});
    generatePassword();
    setStep(1);

    fetchData();
  }, [show]);

  // =========================
  //   Reset complet à la fermeture
  // =========================
  useEffect(() => {
    if (show) return;    setStep(1);
    setErrors({});
    setPasswordDisplay("");
    setPasswordCopied(false);
    setData({
      first_name: "",
      last_name: "",
      matricule: "",
      cin: "",
      telephone: "",
      situation_familiale: "celibataire",
      fonction: "",
      gare: "",
      district: "",
      password: "",
      parents: [],
      enfants: [],
    });
    setValidationStatus({
      matricule: { checking: false, available: null, message: "" },
      cin: { checking: false, available: null, message: "" },
      telephone: { checking: false, available: null, message: "" },
    });
  }, [show]);

  // =========================
  //   Validation serveur d'un champ
  // =========================
  const validateField = async (field, value) => {
    if (!value || value.length < 3) return;    setValidationStatus((prev) => ({
      ...prev,
      [field]: { ...prev[field], checking: true, message: "Vérification..." },
    }));

    try {
      let response;
      switch (field) {
        case "matricule":
          response = await checkMatricule(value);
          break;
        case "telephone":
          response = await checkTelephone(value);
          break;
        case "cin":
          response = await checkCin(value);
          break;
        default:
          return;
      }
      setValidationStatus((prev) => ({
        ...prev,
        [field]: {
          checking: false,
          available: response.data.available,
          message: response.data.message,
        },
      }));

      if (!response.data.available) {
        setErrors((prev) => ({ ...prev, [field]: response.data.message }));
      } else {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    } catch (error) {
      console.error("❌ Erreur validation champ:", field, error);
      setValidationStatus((prev) => ({
        ...prev,
        [field]: {
          checking: false,
          available: null,
          message: "Erreur de vérification",
        },
      }));
    }
  };

  // =========================
  //   Handlers matricule / téléphone
  // =========================
  const handleMatriculeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setData((prev) => ({ ...prev, matricule: value }));

    if (!value) {
      setValidationStatus((prev) => ({
        ...prev,
        matricule: { checking: false, available: null, message: "" },
      }));
      setErrors((prev) => ({ ...prev, matricule: undefined }));
      return;
    }

    if (value.length === 8) {
      validateField("matricule", value);
    }
  };

  const handleTelephoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setData((prev) => ({ ...prev, telephone: value }));

    if (!value) {
      setValidationStatus((prev) => ({
        ...prev,
        telephone: { checking: false, available: null, message: "" },
      }));
      setErrors((prev) => ({ ...prev, telephone: undefined }));
      return;
    }

    if (value.length === 8) {
      validateField("telephone", value);
    }
  };

  const handleCinChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setData((prev) => ({ ...prev, cin: value }));

    if (!value) {
      setValidationStatus((prev) => ({
        ...prev,
        cin: { checking: false, available: null, message: "" },
      }));
      setErrors((prev) => ({ ...prev, cin: undefined }));
      return;
    }

    if (value.length === 8) {
      validateField("cin", value);
    }
  };

  // =========================
  //   Validation Step 1
  // =========================
  const validateStep1 = () => {    const newErrors = {};

    if (!data.first_name.trim()) newErrors.first_name = "Prénom requis";
    if (!data.last_name.trim()) newErrors.last_name = "Nom requis";

    if (!data.matricule.trim()) newErrors.matricule = "Matricule requis";
    else if (!/^\d{8}$/.test(data.matricule))
      newErrors.matricule = "Matricule : 8 chiffres requis";
    else if (validationStatus.matricule.available === false)
      newErrors.matricule = validationStatus.matricule.message;

    if (!data.cin.trim()) newErrors.cin = "CIN requis";
    else if (!/^\d{8}$/.test(data.cin))
      newErrors.cin = "CIN : 8 chiffres requis";
    else if (validationStatus.cin.available === false)
      newErrors.cin = validationStatus.cin.message;

    if (!data.telephone.trim()) newErrors.telephone = "Téléphone requis";
    else if (!/^\d{8}$/.test(data.telephone))
      newErrors.telephone = "Téléphone : 8 chiffres requis";
    else if (validationStatus.telephone.available === false)
      newErrors.telephone = validationStatus.telephone.message;

    if (!passwordCopied)
      newErrors.password = "Veuillez copier et confirmer le mot de passe";

    setErrors((prev) => ({ ...prev, ...newErrors }));

    const ok = Object.keys(newErrors).length === 0;    return ok;
  };

  // =========================
  //   Validation Step 2
  // =========================
  const validateStep2 = () => {    const newErrors = {};

    if (!data.fonction) newErrors.fonction = "Fonction requise";
    if (!data.gare) newErrors.gare = "Gare requise";
    if (!data.district) newErrors.district = "District requis";

    setErrors((prev) => ({ ...prev, ...newErrors }));

    const ok = Object.keys(newErrors).length === 0;    return ok;
  };

  // =========================
  //   Validation Step 3 (Parents + Enfants)
  // =========================
  const validateStep3 = () => {    const newErrors = { parents: [], enfants: [] };

    // Parents : toujours obligatoires
    (data.parents || []).forEach((p, idx) => {
      const pErr = {};
      if (!p.prenom || !p.prenom.trim()) {
        pErr.prenom = "Prénom du parent obligatoire";
      }
      if (!p.date_naissance) {
        pErr.date_naissance = "Date de naissance obligatoire";
      }
      if (!p.situation) {
        pErr.situation = "Situation obligatoire";
      }
      newErrors.parents[idx] = pErr;
    });

    // Enfants : si marie/divorce et enfants saisis, champs obligatoires
    if (data.situation_familiale === "marie" || data.situation_familiale === "divorce") {
      (data.enfants || []).forEach((e, idx) => {
        const eErr = {};
        if (!e.prenom || !e.prenom.trim()) {
          eErr.prenom = "Prénom de l'enfant obligatoire";
        }
        if (!e.date_naissance) {
          eErr.date_naissance = "Date de naissance obligatoire";
        }
        if (!e.sexe) {
          eErr.sexe = "Sexe obligatoire";
        }
        newErrors.enfants[idx] = eErr;
      });
    }

    // Nettoyer les indices vides
    const parentsHasError = (newErrors.parents || []).some(
      (p) => p && Object.keys(p).length > 0
    );
    const enfantsHasError = (newErrors.enfants || []).some(
      (e) => e && Object.keys(e).length > 0
    );

    if (!parentsHasError && !enfantsHasError) {
      // pas d'erreurs
      setErrors((prev) => ({ ...prev, parents: [], enfants: [] }));      return true;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));    return false;
  };

  // =========================
  //   Navigation entre steps
  // =========================
  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;

    if (step === 2) {
      // à l'entrée de l'étape 3 → s'assurer qu'on a des parents
      initParentsIfNeeded();
    }

    setStep((s) => {
      const newStep = s + 1;      return newStep;
    });
  };

  const handlePrev = () => {    setStep((s) => {
      const newStep = s - 1;      return newStep;
    });
  };

  // =========================
  //   Parents / Enfants
  // =========================
  const handleParentChange = (index, field, value) => {
    setData((prev) => {
      const parents = [...(prev.parents || [])];
      parents[index] = {
        ...parents[index],
        [field]: value,
        nom: prev.last_name || parents[index].nom,
      };
      return { ...prev, parents };
    });
  };

  const addEnfant = () => {
    if (
      data.situation_familiale !== "marie" &&
      data.situation_familiale !== "divorce"
    ) {      return;
    }

    setData((prev) => {
      const nomFamille = prev.last_name || "";      return {
        ...prev,
        enfants: [
          ...(prev.enfants || []),
          {
            nom: nomFamille,
            prenom: "",
            date_naissance: "",
            sexe: "",
            classe: "",
            etablissement: "",
          },
        ],
      };
    });
  };

  const handleEnfantChange = (index, field, value) => {
    setData((prev) => {
      const enfants = [...(prev.enfants || [])];
      enfants[index] = {
        ...enfants[index],
        [field]: value,
        nom: prev.last_name || enfants[index].nom,
      };
      return { ...prev, enfants };
    });
  };

  const removeEnfant = (index) => {
    setData((prev) => {
      const enfants = [...(prev.enfants || [])];
      enfants.splice(index, 1);
      return { ...prev, enfants };
    });
  };

  // =========================
  //   Copie mot de passe
  // =========================
  const handleCopyPassword = () => {
    if (!passwordDisplay) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(passwordDisplay)
        .then(() => {
          setPasswordCopied(true);
          setErrors((e) => ({ ...e, password: undefined }));
        })
        .catch(() => {});
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = passwordDisplay;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setPasswordCopied(true);
        setErrors((e) => ({ ...e, password: undefined }));
      } catch (e) {}
      document.body.removeChild(textarea);
    }
  };

  // =========================
  //   Envoi final
  // =========================
  const finish = async () => {
    // on valide aussi l'étape 3 avant d'envoyer
    if (!validateStep3()) {      return;
    }

    // garantir au moins des parents
    const currentData = {
      ...data,
      parents:
        data.parents && data.parents.length > 0
          ? data.parents
          : [
              {
                type_parent: "pere",
                nom: data.last_name || "",
                prenom: "",
                date_naissance: "",
                situation: "vivant",
              },
              {
                type_parent: "mere",
                nom: data.last_name || "",
                prenom: "",
                date_naissance: "",
                situation: "vivant",
              },
            ],
    };

    try {
      const payload = {
        ...currentData,
        fonction: currentData.fonction || null,
        gare: currentData.gare || null,
        district: currentData.district || null,
      };

      if (currentData.situation_familiale === "celibataire") {
        payload.enfants = [];      } else {      }
      await onSave(payload);

      addNotification({
        type: "success",
        title: "Succès",
        message: "Adhérent créé avec succès.",
        delay: 4000,
      });      setStep(1);
      setErrors({});
      setPasswordDisplay("");
      setPasswordCopied(false);
      setData({
        first_name: "",
        last_name: "",
        matricule: "",
        cin: "",
        telephone: "",
        situation_familiale: "celibataire",
        fonction: "",
        gare: "",
        district: "",
        password: "",
        parents: [],
        enfants: [],
      });
      onHide && onHide();
    } catch (err) {
      if (err.response && err.response.data) {
        const backendErrors = err.response.data;
        console.error("🛑 Erreurs backend:", backendErrors);
        setErrors((prev) => ({ ...prev, ...backendErrors }));
        addNotification({
          type: "error",
          title: "Erreur",
          message:
            backendErrors.detail ||
            "Une erreur est survenue lors de l’enregistrement.",
          delay: 7000,
        });
      } else {
        console.error("❌ Erreur inattendue:", err);
        addNotification({
          type: "error",
          title: "Erreur réseau",
          message:
            "Impossible de contacter le serveur. Vérifiez votre connexion.",
          delay: 7000,
        });
      }
    }
  };

  // =========================
  //   Helpers d'erreurs parents / enfants
  // =========================
  const getParentError = (index, field) =>
    errors.parents && errors.parents[index]
      ? errors.parents[index][field]
      : undefined;

  const getEnfantError = (index, field) =>
    errors.enfants && errors.enfants[index]
      ? errors.enfants[index][field]
      : undefined;

  // =========================
  //   Rendu
  // =========================
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 1
            ? "Étape 1 : Informations personnelles"
            : step === 2
            ? "Étape 2 : Informations professionnelles"
            : "Étape 3 : Famille"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ==== ÉTAPE 1 ==== */}
        {step === 1 && (
          <Form noValidate>
            <Row className="mb-3">
              <Col>
                <Form.Label>Matricule (8 chiffres) *</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={data.matricule}
                  onChange={handleMatriculeChange}
                  isInvalid={
                    !!errors.matricule ||
                    validationStatus.matricule.available === false
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {errors.matricule}
                </Form.Control.Feedback>
                {validationStatus.matricule.checking && (
                  <Form.Text className="text-muted">
                    ⏳ Vérification...
                  </Form.Text>
                )}
                {validationStatus.matricule.available && (
                  <Form.Text className="text-success">
                    ✅ {validationStatus.matricule.message}
                  </Form.Text>
                )}
                {validationStatus.matricule.available === false && (
                  <Form.Text className="text-danger">
                    ❌ {validationStatus.matricule.message}
                  </Form.Text>
                )}
              </Col>
              <Col>
                <Form.Label>CIN (8 chiffres) *</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={data.cin}
                  onChange={handleCinChange}
                  isInvalid={
                    !!errors.cin ||
                    validationStatus.cin.available === false
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cin}
                </Form.Control.Feedback>
                {validationStatus.cin.checking && (
                  <Form.Text className="text-muted">
                    Verification...
                  </Form.Text>
                )}
                {validationStatus.cin.available && (
                  <Form.Text className="text-success">
                    {validationStatus.cin.message}
                  </Form.Text>
                )}
                {validationStatus.cin.available === false && (
                  <Form.Text className="text-danger">
                    {validationStatus.cin.message}
                  </Form.Text>
                )}
              </Col>
              <Col>
                <Form.Label>Téléphone (8 chiffres) *</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={data.telephone}
                  onChange={handleTelephoneChange}
                  isInvalid={
                    !!errors.telephone ||
                    validationStatus.telephone.available === false
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {errors.telephone}
                </Form.Control.Feedback>
                {validationStatus.telephone.checking && (
                  <Form.Text className="text-muted">
                    ⏳ Vérification...
                  </Form.Text>
                )}
                {validationStatus.telephone.available && (
                  <Form.Text className="text-success">
                    ✅ {validationStatus.telephone.message}
                  </Form.Text>
                )}
                {validationStatus.telephone.available === false && (
                  <Form.Text className="text-danger">
                    ❌ {validationStatus.telephone.message}
                  </Form.Text>
                )}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Label>Prénom *</Form.Label>
                <Form.Control
                  value={data.first_name}
                  onChange={(e) =>
                    setData((prev) => ({ ...prev, first_name: e.target.value }))
                  }
                  isInvalid={!!errors.first_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.first_name}
                </Form.Control.Feedback>
              </Col>
              <Col>
                <Form.Label>Nom *</Form.Label>
                <Form.Control
                  value={data.last_name}
                  onChange={(e) =>
                    setData((prev) => ({ ...prev, last_name: e.target.value }))
                  }
                  isInvalid={!!errors.last_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.last_name}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Label>Situation familiale *</Form.Label>
                <Form.Select
                  value={data.situation_familiale}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      situation_familiale: e.target.value,
                    }))
                  }
                >
                  <option value="celibataire">Célibataire</option>
                  <option value="marie">Marié(e)</option>
                  <option value="divorce">Divorcé(e)</option>
                </Form.Select>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Label>Mot de passe généré *</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    readOnly
                    value={passwordDisplay}
                    isInvalid={!!errors.password}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={handleCopyPassword}
                  >
                    📋 Copier
                  </Button>
                  <Button type="button" variant="outline-danger" onClick={generatePassword}>
                    🔁 Autre
                  </Button>
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                  {Array.isArray(errors.password)
                    ? errors.password[0]
                    : errors.password}
                </Form.Control.Feedback>
                <Form.Check
                  className="mt-2"
                  checked={passwordCopied}
                  onChange={(e) => setPasswordCopied(e.target.checked)}
                  label="✅ J'ai bien copié le mot de passe"
                />
              </Col>
            </Row>
          </Form>
        )}

        {/* ==== ÉTAPE 2 ==== */}
        {step === 2 && (
          <>
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" />
              </div>
            ) : (
              <Form>
                <Row className="mb-3">
                  <Col>
                    <Form.Label>Fonction *</Form.Label>
                    <Form.Select
                      value={data.fonction}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          fonction: e.target.value,
                        }))
                      }
                      isInvalid={!!errors.fonction}
                    >
                      <option value="">-- Sélectionner --</option>
                      {fonctions.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nom}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.fonction}
                    </Form.Control.Feedback>
                  </Col>
                  <Col>
                    <Form.Label>Gare *</Form.Label>
                    <Form.Select
                      value={data.gare}
                      onChange={(e) =>
                        setData((prev) => ({ ...prev, gare: e.target.value }))
                      }
                      isInvalid={!!errors.gare}
                    >
                      <option value="">-- Sélectionner --</option>
                      {gares.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.nom}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.gare}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Label>District *</Form.Label>
                    <Form.Select
                      value={data.district}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          district: e.target.value,
                        }))
                      }
                      isInvalid={!!errors.district}
                    >
                      <option value="">-- Sélectionner --</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.nom}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.district}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </Form>
            )}
          </>
        )}

        {/* ==== ÉTAPE 3 ==== */}
        {step === 3 && (
          <>
            <h5>Parents</h5>
            {data.parents.map((parent, index) => (
              <div
                key={index}
                className="border rounded p-2 mb-2"
                style={{ background: "#f8f9fa" }}
              >
                <strong>
                  {parent.type_parent === "pere" ? "Père" : "Mère"}
                </strong>
                <Row className="mt-2">
                  <Col>
                    <Form.Group>
                      <Form.Label>Nom</Form.Label>
                      <Form.Control value={parent.nom} disabled />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Prénom *</Form.Label>
                      <Form.Control
                        value={parent.prenom}
                        onChange={(e) =>
                          handleParentChange(index, "prenom", e.target.value)
                        }
                        isInvalid={!!getParentError(index, "prenom")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {getParentError(index, "prenom")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col>
                    <Form.Group>
                      <Form.Label>Date de naissance *</Form.Label>
                      <Form.Control
                        type="date"
                        value={parent.date_naissance || ""}
                        onChange={(e) =>
                          handleParentChange(
                            index,
                            "date_naissance",
                            e.target.value
                          )
                        }
                        isInvalid={!!getParentError(index, "date_naissance")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {getParentError(index, "date_naissance")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Situation *</Form.Label>
                      <Form.Select
                        value={parent.situation}
                        onChange={(e) =>
                          handleParentChange(
                            index,
                            "situation",
                            e.target.value
                          )
                        }
                        isInvalid={!!getParentError(index, "situation")}
                      >
                        <option value="vivant">Vivant</option>
                        <option value="decede">Décédé</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {getParentError(index, "situation")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}

            <hr />

            {data.situation_familiale === "celibataire" ? (
              <p>Célibataire : aucune information enfants requise.</p>
            ) : (
              <>
                <h5>Enfants</h5>
                {data.enfants.map((enf, index) => (
                  <div
                    key={index}
                    className="border rounded p-2 mb-2"
                    style={{ background: "#f8f9fa" }}
                  >
                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>Nom</Form.Label>
                          <Form.Control value={enf.nom} disabled />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group>
                          <Form.Label>Prénom *</Form.Label>
                          <Form.Control
                            value={enf.prenom}
                            onChange={(e) =>
                              handleEnfantChange(
                                index,
                                "prenom",
                                e.target.value
                              )
                            }
                            isInvalid={!!getEnfantError(index, "prenom")}
                          />
                          <Form.Control.Feedback type="invalid">
                            {getEnfantError(index, "prenom")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Form.Group>
                          <Form.Label>Date de naissance *</Form.Label>
                          <Form.Control
                            type="date"
                            value={enf.date_naissance || ""}
                            onChange={(e) =>
                              handleEnfantChange(
                                index,
                                "date_naissance",
                                e.target.value
                              )
                            }
                            isInvalid={!!getEnfantError(index, "date_naissance")}
                          />
                          <Form.Control.Feedback type="invalid">
                            {getEnfantError(index, "date_naissance")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group>
                          <Form.Label>Sexe *</Form.Label>
                          <Form.Select
                            value={enf.sexe || ""}
                            onChange={(e) =>
                              handleEnfantChange(index, "sexe", e.target.value)
                            }
                            isInvalid={!!getEnfantError(index, "sexe")}
                          >
                            <option value="">--</option>
                            <option value="M">Masculin</option>
                            <option value="F">Féminin</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {getEnfantError(index, "sexe")}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Form.Group>
                          <Form.Label>Classe</Form.Label>
                          <Form.Control
                            value={enf.classe || ""}
                            onChange={(e) =>
                              handleEnfantChange(
                                index,
                                "classe",
                                e.target.value
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group>
                          <Form.Label>Établissement</Form.Label>
                          <Form.Control
                            value={enf.etablissement || ""}
                            onChange={(e) =>
                              handleEnfantChange(
                                index,
                                "etablissement",
                                e.target.value
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="mt-2 text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeEnfant(index)}
                      >
                        Supprimer cet enfant
                      </Button>
                    </div>
                  </div>
                ))}

                <Button variant="outline-primary" size="sm" onClick={addEnfant}>
                  ➕ Ajouter un enfant
                </Button>
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step > 1 && (
          <Button variant="secondary" onClick={handlePrev}>
            ⬅️ Précédent
          </Button>
        )}
        {step < 3 && (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={step === 1 && !passwordCopied}
          >
            Suivant ➡️
          </Button>
        )}
        {step === 3 && (
          <Button
            variant="success"
            onClick={finish}
            disabled={!passwordCopied}
          >
            Enregistrer
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
